import { useTranslations } from "use-intl";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { Link } from "@/components/catalyst/link";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import { IncidentRecord } from "@/app/Incidents/GraphQL/incidentRecordQuery";

function joinPresent(values: (string | null | undefined)[], separator: string) {
  const present = values.filter(Boolean);
  return present.length > 0 ? present.join(separator) : null;
}

export function IncidentJobCard({ incident }: { incident: IncidentRecord }) {
  const t = useTranslations("incident.details");
  const tVehicle = useTranslations("vehicle");
  const tLicense = useTranslations("proDriver.licenseType");
  const { isAdmin } = useTenant();
  const assignment = incident.workOrderAssignment;
  const workOrder = incident.workOrder;
  const driver = incident.proDriver;
  const vehicle = workOrder.relocation?.vehicle;

  const vehicleDetails = joinPresent([vehicle?.brand, vehicle?.model], " ");

  return (
    <RecordScreenCard
      title={t("job")}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          <Link
            className="font-medium text-blue-600 hover:text-blue-800"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
            params={(prev) => ({ ...prev, id: workOrder.id })}
          >
            {workOrder.reference}
          </Link>
          {isAdmin ? <span>· {incident.supplier.name}</span> : null}
        </span>
      }
    >
      <div className="space-y-4">
        <div className="grid items-start gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-[1fr_auto_1fr_minmax(0,14rem)]">
          <Stop
            label={t("pickup")}
            address={workOrder.departureAddress.formatted_address_line_1}
            scheduled={assignment.pickup_date}
            actual={formatDateTimeLocal(assignment.driver_pick_up_at)}
            actualLabel={t("pickedUpOn")}
            pendingLabel={t("notPickedUp")}
          />
          <ArrowRightIcon className="mt-6 hidden size-5 text-gray-300 md:block" />
          <Stop
            label={t("delivery")}
            address={workOrder.deliveryAddress.formatted_address_line_1}
            scheduled={assignment.delivery_date}
            actual={formatDateTimeLocal(assignment.driver_delivered_at)}
            actualLabel={t("deliveredOn")}
            pendingLabel={t("notDelivered")}
          />
          <div className="space-y-1 md:border-l md:border-gray-200 md:pl-4">
            <p className="text-xs text-gray-500">{t("vehicle")}</p>
            <p className="text-sm font-medium text-gray-900">
              {tVehicle(`type.${workOrder.vehicle_type}`)}
            </p>
            {vehicleDetails ? (
              <p className="text-sm text-gray-600">{vehicleDetails}</p>
            ) : null}
          </div>
        </div>

        <AdminSection>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t("driver")} value={driver.name} />
            <Field label={t("email")} value={driver.email} />
            <Field label={t("phone")} value={driver.phone} />
            <Field
              label={t("licence")}
              value={joinPresent(
                [
                  driver.license_number,
                  driver.license_issuer,
                  tLicense(driver.license_type),
                ],
                " · ",
              )}
            />
          </dl>
        </AdminSection>
      </div>
    </RecordScreenCard>
  );
}

function Stop({
  label,
  address,
  scheduled,
  actual,
  actualLabel,
  pendingLabel,
}: {
  label: string;
  address: string | null | undefined;
  scheduled: string | null | undefined;
  actual: string | null;
  actualLabel: string;
  pendingLabel: string;
}) {
  const t = useTranslations("incident.details");

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{address ?? "--"}</p>
      <p className="text-sm text-gray-600">
        {actual ? `${actualLabel} ${actual}` : pendingLabel}
      </p>
      {scheduled ? (
        <p className="text-xs text-gray-500">
          {t("scheduledFor", { date: formatDate(scheduled) })}
        </p>
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="break-words text-sm text-gray-900">{value ?? "--"}</dd>
    </div>
  );
}
